<template>
	<slot />
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, watch } from "vue";
import { useMap } from "vue-maplibre-gl";
import type {
	Map as MaplibreMap,
	MapGeoJSONFeature,
	MapMouseEvent,
	PointLike,
} from "maplibre-gl";

import { featureIdOf } from "./geometry";
import type { MapSelectPayload, SelectId, SelectLayer } from "./types";

/**
 * The one answer to "what did the operator click".
 *
 * A surface declares its layers, ordered by what should win, and never writes a
 * `queryRenderedFeatures` ladder again: this widget runs the query, resolves the
 * id, computes `additive` from the gesture the ledger itself reads, and emits one
 * payload. It draws nothing, which is why its template is only a slot — it is a
 * `<mgl-map>` child that listens.
 *
 * `target` picks the query shape. A point layer's dot is a few pixels wide, so a
 * click near it has to be widened into a tolerance box; a fill layer is what the
 * operator aimed at, so the click itself is the query and hitting the fill
 * anywhere inside counts.
 */

const props = withDefaults(
	defineProps<{
		/** The declared layers, in the surface's own order. */
		layers: SelectLayer[];
		/** How far, in pixels, a `"box"` hit may be from the pointer. */
		hitTolerance?: number;
		/** True while the page owns the click for something else (painting a
		 * cell, picking a form target): the widget then consumes nothing. */
		disabled?: boolean;
	}>(),
	{ hitTolerance: 6, disabled: false },
);

const emit = defineEmits<{
	select: [payload: MapSelectPayload];
	clear: [];
}>();

const mapInstance = useMap();
let map: MaplibreMap | null = null;

/** Lowest priority first; ties keep the declaration order (`sort` is stable), so
 * a surface orders equal-priority layers by listing them in order. */
const ordered = computed(() =>
	[...props.layers].sort((a, b) => a.priority - b.priority),
);

/** The first id a layer's hits resolve to — a numeric row id, or the hex cell
 * index a grid's layer names. A hit that resolves to no id is skipped rather
 * than selected as `NaN`; later hits on the same layer may still name one. */
function firstId(
	layer: SelectLayer,
	hits: MapGeoJSONFeature[],
): SelectId | null {
	for (const hit of hits) {
		const id = featureIdOf(layer, hit.properties ?? {}, hit.id);
		if (id !== null) return id;
	}
	return null;
}

function onClick(event: MapMouseEvent): void {
	if (props.disabled) return;
	const active = map;
	if (!active) return;
	const point = event.point;
	for (const layer of ordered.value) {
		// A layer the style has not added yet is not an error: a surface
		// declares its policy before its data has to exist.
		if (!active.getLayer(layer.layerId)) continue;
		const query: PointLike | [PointLike, PointLike] =
			layer.target === "point"
				? point
				: [
						[point.x - props.hitTolerance, point.y - props.hitTolerance],
						[point.x + props.hitTolerance, point.y + props.hitTolerance],
					];
		const id = firstId(
			layer,
			active.queryRenderedFeatures(query, { layers: [layer.layerId] }),
		);
		if (id === null) continue;
		const gesture = event.originalEvent as MouseEvent | undefined;
		emit("select", {
			kind: layer.kind,
			id,
			additive: Boolean(
				gesture?.ctrlKey || gesture?.metaKey || gesture?.shiftKey,
			),
		});
		return;
	}
	// No declared layer was hit: this click means "deselect", the same answer a
	// ledger gives to clicking empty space.
	emit("clear");
}

// The map arrives after setup, and its style may be replaced under us, so the
// handler is bound to whatever instance `useMap()` currently hands out rather
// than captured once.
watch(
	() => mapInstance.map,
	(instance) => {
		if (instance === map) return;
		if (map) map.off("click", onClick);
		map = instance ?? null;
		if (map) map.on("click", onClick);
	},
	{ immediate: true },
);

onBeforeUnmount(() => {
	if (map) map.off("click", onClick);
});
</script>
